/**
 * ThemedCard Component
 * 
 * Card container that adapts its appearance based on the active immersive theme.
 * Supports all 5 themes with unique visual characteristics.
 */

import React, { useState } from 'react';
import { useImmersiveTheme } from '../../hooks/useImmersiveTheme';
import { ImmersiveThemeType, ThemedCardProps } from '../../types/immersive-theme.types';
import {
  neumorphismCard,
  neumorphismCardHover,
  neumorphismCardInset,
} from './theme-styles/neumorphism';
import {
  brutalistCard,
  brutalistCardHover,
  brutalistCardHeader,
} from './theme-styles/brutalist';
import {
  retroTerminalCard,
  retroTerminalCardHeader,
  retroTerminalCardTitle,
  retroTerminalCardTitlePrefix,
  retroTerminalScanlines,
} from './theme-styles/retro-terminal';
import {
  organicNatureCard,
  organicNatureCardHover,
  organicNatureCardHeader,
  organicNatureCardTitle,
  organicNatureCardWithTexture,
} from './theme-styles/organic-nature';
import {
  luxuryGoldCard,
  luxuryGoldCardHover,
  luxuryGoldCardHeader,
  luxuryGoldCardTitle,
  luxuryGoldCardElevated,
  luxuryGoldCardGoldBorder,
} from './theme-styles/luxury-gold';

// Card styles per theme
const cardStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {
    background: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    padding: '24px',
    transition: 'all 0.2s ease',
  },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismCard,
  [ImmersiveThemeType.BRUTALIST]: brutalistCard,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalCard,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureCard,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldCard,
};

// Hover styles per theme
const hoverStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: { boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)', transform: 'translateY(-2px)' },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismCardHover,
  [ImmersiveThemeType.BRUTALIST]: brutalistCardHover,
  [ImmersiveThemeType.RETRO_TERMINAL]: { boxShadow: 'var(--term-glow)' },
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureCardHover,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldCardHover,
};

// Header styles per theme
const headerStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {
    borderBottom: '1px solid #e5e7eb',
    paddingBottom: '16px',
    marginBottom: '16px',
  },
  [ImmersiveThemeType.NEUMORPHISM]: {
    borderBottom: '1px solid var(--neu-bg-secondary)',
    paddingBottom: 'var(--neu-spacing-md)',
    marginBottom: 'var(--neu-spacing-md)',
  },
  [ImmersiveThemeType.BRUTALIST]: brutalistCardHeader,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalCardHeader,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureCardHeader,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldCardHeader,
};

// Title styles per theme
const titleStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#111827',
    margin: 0,
  },
  [ImmersiveThemeType.NEUMORPHISM]: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: 'var(--neu-text)',
    margin: 0,
  },
  [ImmersiveThemeType.BRUTALIST]: {
    fontSize: '1.25rem',
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: '-0.02em',
    margin: 0,
  },
  [ImmersiveThemeType.RETRO_TERMINAL]: {
    ...retroTerminalCardTitle,
    fontSize: '1rem',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    margin: 0,
  },
  [ImmersiveThemeType.ORGANIC_NATURE]: {
    ...organicNatureCardTitle,
    margin: 0,
  },
  [ImmersiveThemeType.LUXURY_GOLD]: {
    ...luxuryGoldCardTitle,
    margin: 0,
  },
};

// Subtitle styles per theme
const subtitleStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {
    fontSize: '0.875rem',
    color: '#6b7280',
    marginTop: '4px',
  },
  [ImmersiveThemeType.NEUMORPHISM]: {
    fontSize: '0.875rem',
    color: 'var(--neu-text-secondary)',
    marginTop: '4px',
  },
  [ImmersiveThemeType.BRUTALIST]: {
    fontSize: '0.875rem',
    color: 'var(--brut-text-secondary)',
    marginTop: '4px',
    fontWeight: 700,
  },
  [ImmersiveThemeType.RETRO_TERMINAL]: {
    fontSize: '0.75rem',
    color: 'var(--term-text-dim)',
    marginTop: '4px',
    fontFamily: 'var(--term-font)',
  },
  [ImmersiveThemeType.ORGANIC_NATURE]: {
    fontSize: '0.875rem',
    color: 'var(--org-text-secondary)',
    marginTop: '4px',
  },
  [ImmersiveThemeType.LUXURY_GOLD]: {
    fontSize: '0.875rem',
    color: 'var(--lux-text-secondary)',
    marginTop: '4px',
    letterSpacing: 'var(--lux-letter-spacing)',
  },
};

// Padding variants
const paddingStyles: Record<string, React.CSSProperties> = {
  none: { padding: 0 },
  sm: { padding: '12px' },
  md: { padding: '20px' },
  lg: { padding: '32px' },
};

export const ThemedCard: React.FC<ThemedCardProps> = ({
  title,
  subtitle,
  footer,
  header,
  hoverable = false,
  bordered = false,
  elevated = false,
  padding = 'md',
  onClick,
  className,
  children,
}) => {
  const { theme, reducedMotion } = useImmersiveTheme();
  const [isHovered, setIsHovered] = useState(false);

  // Compute styles
  const computedStyles: React.CSSProperties = {
    ...cardStyles[theme],
    ...paddingStyles[padding],
    ...(hoverable && isHovered && !reducedMotion ? hoverStyles[theme] : {}),
    ...(bordered && theme === ImmersiveThemeType.LUXURY_GOLD ? luxuryGoldCardGoldBorder : {}),
    ...(elevated && theme === ImmersiveThemeType.LUXURY_GOLD ? luxuryGoldCardElevated : {}),
    ...(elevated && theme === ImmersiveThemeType.NEUMORPHISM ? { boxShadow: 'var(--neu-shadow-out-lg)' } : {}),
    ...(onClick ? { cursor: 'pointer' } : {}),
    ...(reducedMotion ? { transition: 'none' } : {}),
    position: 'relative',
    overflow: 'hidden',
  };

  // Footer styles
  const footerStyles: React.CSSProperties = {
    borderTop: theme === ImmersiveThemeType.BRUTALIST 
      ? 'var(--brut-border)' 
      : theme === ImmersiveThemeType.RETRO_TERMINAL
        ? 'var(--term-border-dim)'
        : theme === ImmersiveThemeType.LUXURY_GOLD
          ? 'var(--lux-border)'
          : '1px solid rgba(0, 0, 0, 0.1)',
    paddingTop: '16px',
    marginTop: '16px',
  };

  // Render title with theme-specific prefix for RETRO_TERMINAL
  const renderTitle = () => {
    if (!title) return null;

    if (theme === ImmersiveThemeType.RETRO_TERMINAL) {
      return (
        <div style={retroTerminalCardTitle}>
          <span style={retroTerminalCardTitlePrefix}>[</span>
          <span>{title}</span>
          <span style={retroTerminalCardTitlePrefix}>]</span>
        </div>
      );
    }

    return <h3 style={titleStyles[theme]}>{title}</h3>;
  };

  return (
    <div
      style={computedStyles}
      className={className}
      onClick={onClick}
      onMouseEnter={() => hoverable && setIsHovered(true)}
      onMouseLeave={() => hoverable && setIsHovered(false)}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      {/* Scanlines overlay for RETRO_TERMINAL */}
      {theme === ImmersiveThemeType.RETRO_TERMINAL && (
        <div style={retroTerminalScanlines} aria-hidden="true" />
      )}

      {/* Custom header or default header with title/subtitle */}
      {(header || title || subtitle) && (
        <div style={headerStyles[theme]}>
          {header || (
            <>
              {renderTitle()}
              {subtitle && <p style={subtitleStyles[theme]}>{subtitle}</p>}
            </>
          )}
        </div>
      )}

      {/* Card content */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {children}
      </div>

      {/* Footer */}
      {footer && (
        <div style={footerStyles}>
          {footer}
        </div>
      )}
    </div>
  );
};

// Inset variant for nested cards (especially useful for NEUMORPHISM)
export const ThemedCardInset: React.FC<ThemedCardProps> = (props) => {
  const { theme } = useImmersiveTheme();

  const insetStyles: React.CSSProperties = {
    ...(theme === ImmersiveThemeType.NEUMORPHISM ? neumorphismCardInset : {}),
    ...(theme === ImmersiveThemeType.RETRO_TERMINAL ? { 
      background: 'var(--term-bg-secondary)', 
      border: 'var(--term-border-dim)',
      padding: 'var(--term-spacing-md)',
    } : {}),
    ...(theme === ImmersiveThemeType.LUXURY_GOLD ? {
      background: 'var(--lux-bg-secondary)',
      border: 'var(--lux-border)',
      borderRadius: 'var(--lux-radius)',
      padding: 'var(--lux-spacing-md)',
    } : {}),
  };

  return (
    <div style={insetStyles} className={props.className}>
      {props.children}
    </div>
  );
};

export default ThemedCard;
